<template>
  <div
    class="rs-spin-button"
  >
    <label
      v-if="label"
      class="label"
    >
      {{ label }}
      <sup
        v-if="required"
        class="mt-2"
      >*</sup>
      <slot name="custom-component" />
    </label>
    <Field
      ref="validatorRef"
      v-slot="{ errors }"
      v-model="internalValue"
      class="veeValidate"
      as="div"
      :name="label ? label : name ? name : 'field'"
      :rules="rules"
    >
      <div
        :id="`${id}-field`"
        class="input-container"
      >
        <div
          class="minus-input-container"
          :class="{ disabled: minusDisabled }"
          @click="minusDisabled || onInput((internalValue ?? 0) - step)"
        >
          <RsIcon
            class="minus-input m-auto"
            icon="minus"
          />
        </div>
        <div class="number-input-container">
          <input
            :value="internalValue"
            type="number"
            class="form-control number-input p-2"
            :disabled="disabled"
            @keydown="isNumberKey"
            @change="onInputChange"
          />
        </div>
        <div
          class="plus-input-container"
          :class="{ disabled: plusDisabled }"
          @click="plusDisabled || onInput((internalValue ?? 0) + step)"
        >
          <RsIcon
            class="plus-input m-auto"
            icon="plus"
          />
        </div>
      </div>
      <div
        v-if="errors.length"
        class="text--error"
      >
        {{ errors[0] }}
      </div>
    </Field>
  </div>
</template>
<script lang="ts">
import { ComponentPublicInstance, defineComponent, PropType } from 'vue';
import { Field, defineRule } from 'vee-validate';
import {
  max_value as maxValue,
  min_value as minValue,
} from '@vee-validate/rules';

defineRule('maxValue', (value: unknown, params: [number], ctx: { field: string }) => (maxValue(value, params) ? true : `${ctx.field} cannot be greater than ${params[0]}`));

defineRule('minValue', (value: unknown, params: [number], ctx: { field: string }) => (minValue(value, params) ? true : `${ctx.field} cannot be less than ${params[0]}`));

export default defineComponent({
  name: 'RsSpinButton',

  components: {
    Field,
  },
  props: {
    id: {
      type: String,
      default: '',
    },
    label: {
      type: String,
      default: '',
    },
    name: {
      type: String,
      default: 'input',
    },
    required: {
      type: Boolean,
      default: false,
    },
    step: {
      type: Number,
      default: 1,
    },
    debounce: {
      type: Number,
      default: 300,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    rules: {
      type: Object,
      default: () => {},
    },
    customErrorMessage: {
      type: String,
      default: '',
    },
    value: {
      type: Number as PropType<number | undefined | null>,
      default: null,
    },
    /**
     * Vue 3 v-model binding. Kept alongside the legacy `value`/`input` pair so
     * both `v-model` (modelValue/update:modelValue) and explicit `:value`+`@input`
     * consumers work. `undefined` means the parent did not bind v-model, so we
     * fall back to `value` (see the `boundValue` computed).
     */
    modelValue: {
      type: Number as PropType<number | undefined | null>,
      default: undefined,
    },
  },

  emits: ['change', 'input', 'update:modelValue'],
  data() {
    return {
      internalValue: null as number | undefined | null,
    };
  },
  computed: {
    /**
     * The externally-bound value, preferring the Vue 3 `modelValue` (v-model)
     * when present and falling back to the legacy `value` prop otherwise.
     */
    boundValue(): number | undefined | null {
      return this.modelValue !== undefined ? this.modelValue : this.value;
    },
    minusDisabled(): boolean {
      // Disabled state has higher priority than rules.
      if (this.disabled) {
        return true;
      }

      // undefined will throw NaN
      if (this.internalValue === undefined) {
        return false;
      }

      // Check for min_value rule.
      if (this.rules?.min_value) {
        return false;
      }

      // Determine if min_value rule is hit
      return (Number(this.internalValue) <= this.rules.min_value);
    },
    plusDisabled(): boolean {
      // Disabled state has higher priority than rules.
      if (this.disabled) {
        return true;
      }

      // undefined will throw NaN
      if (this.internalValue === undefined) {
        return false;
      }

      // Check for max_value rule.
      if (!this.rules?.max_value) {
        return false;
      }

      // Determine if max_value rule is hit
      return (Number(this.internalValue) >= this.rules.max_value);
    },
  },
  watch: {
    boundValue: {
      immediate: true,
      handler(): void {
        this.internalValue = this.boundValue;
      },
    },
  },
  mounted() {
    // add click outside event listener
    const selfElement = document.getElementById(`${this.id}-field`);
    document.addEventListener('click', (event) => {
      const isClickInsideElement = selfElement?.contains(event.target as Node);
      if (!isClickInsideElement) {
        this.triggerFocusout();
      }
    });
    (this.$refs.validatorRef as ComponentPublicInstance & { validate: () => Promise<boolean> }).validate();
  },
  methods: {
    onInputChange(event: Event): void {
      this.onInput((event.target as HTMLInputElement).value);
    },
    isNumberKey(keyEvent: KeyboardEvent): void {
      const { key } = keyEvent;
      if (!((key >= '0' && key <= '9'))
        && key !== 'Backspace'
        && key !== 'Delete'
        && key !== '.'
        && key !== 'ArrowLeft'
        && key !== 'ArrowRight'
      ) {
        keyEvent.preventDefault();
      }
    },
    onInput(val: number | string): void {
      this.triggerFocusin();
      if (typeof val === 'string') {
        this.internalValue = parseFloat(val);
      } else {
        this.internalValue = Number(val.toFixed(2));
      }

      this.$emit('update:modelValue', this.internalValue);
      this.$emit('input', this.internalValue);
      this.$emit('change', this.internalValue);
    },
    triggerFocusin(): void {
      const element = document.getElementById(`${this.id}-field`);
      if (element) {
        const focusinEvent = new Event('focusin', { bubbles: true });
        element.dispatchEvent(focusinEvent);
      }
    },
    triggerFocusout(): void {
      const element = document.getElementById(`${this.id}-field`);
      if (element) {
        const focusoutEvent = new Event('focusout', { bubbles: true });
        element.dispatchEvent(focusoutEvent);
      }
    },
  },
});
</script>
<style lang="scss">
@import "@/styles/_variables.scss";

.rs-spin-button {

  /* Chrome, Safari, Edge, Opera */
  input::-webkit-outer-spin-button,
  input::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }

  /* Firefox */
  input[type=number] {
    -moz-appearance: textfield;
  }

  .input-container {
    display: flex;
    flex-direction: row;
    .number-input {
      border-radius: 0;
      border: 1px solid #C2C6D9;
      text-align: center;
      z-index: 100;

      &:focus {
        border-radius: 0;
        border: 1px solid #C2C6D9;
      }
    }
  }
  .minus-input-container, .number-input-container, .plus-input-container {
    display: flex;
    align-items: center;
    background-color: #FFFFFF;

    &.disabled {
      background-color: #E9ECEF;
    }
  }

  .minus-input-container {
    border: 1px solid #C2C6D9;
    border-right: none;
    border-radius: 6px 0 0 6px;
    width: 40px;
    &:hover {
      cursor: pointer;
    }
  }

  .plus-input-container {
    border: 1px solid #C2C6D9;
    border-left: none;
    border-radius: 0 6px 6px 0;
    width: 40px;
    &:hover {
      cursor: pointer;
    }
  }

  .number-input-container {
    flex-grow: 100;
  }

  .minus-input, .plus-input {
    color: $tertiary-muted;
    font-weight: bold;
  }
}
</style>
